import { useEffect, useState } from "react";
import { Youtube, Instagram, Music2, Copy, Download, ExternalLink, CalendarPlus, Check } from "lucide-react";
import { mediaUrl, getBestTimes, createSchedule } from "../lib/api";
import { toast } from "sonner";

const PLATFORMS = [
  { id: "tiktok", label: "TikTok", icon: Music2, url: "https://www.tiktok.com/upload?lang=en",
    accent: "#25F4EE", spec: "9:16 · MP4 · up to 10 min" },
  { id: "reels", label: "Instagram Reels", icon: Instagram, url: "https://www.instagram.com/",
    accent: "#FF3EA5", spec: "9:16 · MP4 · up to 90 s" },
  { id: "shorts", label: "YouTube Shorts", icon: Youtube, url: "https://www.youtube.com/upload",
    accent: "#E83F3F", spec: "9:16 · MP4 · up to 3 min" },
];

export const PublishHub = ({ clip }) => {
  const [times, setTimes] = useState({});
  const [scheduled, setScheduled] = useState({});
  const [customTime, setCustomTime] = useState("");
  const [customPlatform, setCustomPlatform] = useState("tiktok");
  const captionText = `${clip.caption}\n\n${(clip.hashtags || []).join(" ")}`.trim();

  useEffect(() => { getBestTimes().then(setTimes).catch(() => {}); }, []);

  const copyCaption = () => {
    navigator.clipboard.writeText(captionText);
    toast.success("Caption + hashtags copied");
  };

  const fmtCustom = (val) => {
    const d = new Date(val);
    if (isNaN(d)) return val;
    return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const scheduleCustom = async () => {
    if (!customTime) { toast.error("Pick a date & time first"); return; }
    if (new Date(customTime) < new Date()) { toast.error("Pick a time in the future"); return; }
    const label = fmtCustom(customTime);
    try {
      await createSchedule({ clip_id: clip.id, platform: customPlatform, scheduled_at: label });
      const pl = PLATFORMS.find((p) => p.id === customPlatform);
      toast.success(`Scheduled for ${pl?.label} · ${label}`);
      setCustomTime("");
    } catch { toast.error("Could not schedule"); }
  };

  const downloadVideo = () => {
    if (!clip.video_path) { toast.error("No video for this clip"); return; }
    const a = document.createElement("a");
    a.href = mediaUrl(clip.video_path);
    a.download = `${clip.title.replace(/[^a-z0-9]+/gi, "_")}.mp4`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const openPlatform = (p) => {
    navigator.clipboard.writeText(captionText).catch(() => {});
    downloadVideo();
    window.open(p.url, "_blank", "noopener");
    toast.success(`Video downloaded & caption copied — opening ${p.label}`);
  };

  const schedule = async (p) => {
    const slot = (times[p.id] || ["soon"])[0];
    try {
      await createSchedule({ clip_id: clip.id, platform: p.id, scheduled_at: slot });
      setScheduled((s) => ({ ...s, [p.id]: true }));
      toast.success(`Scheduled for ${p.label} · ${slot}`);
    } catch { toast.error("Could not schedule"); }
  };

  return (
    <div data-testid={`publish-hub-${clip.id}`} className="space-y-3 p-4">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent">Publish · pick a platform</p>
        <button
          data-testid={`publish-copy-${clip.id}`}
          onClick={copyCaption}
          className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-white"
        >
          <Copy className="h-3 w-3" /> Copy caption
        </button>
      </div>

      <div className="space-y-2">
        {PLATFORMS.map((p) => {
          const Icon = p.icon;
          const best = (times[p.id] || [])[0];
          return (
            <div key={p.id} className="flex items-center gap-3 border border-border bg-background p-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center border border-border" style={{ color: p.accent }}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white">{p.label}</p>
                <p className="font-mono text-[10px] text-muted-foreground">
                  {best ? `best: ${best}` : p.spec}
                </p>
              </div>
              <button
                data-testid={`publish-schedule-${p.id}-${clip.id}`}
                onClick={() => schedule(p)}
                className="flex items-center gap-1.5 border border-border px-2.5 py-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground transition-colors hover:border-accent hover:text-accent"
                title={best ? `Schedule for ${best}` : "Schedule"}
              >
                {scheduled[p.id] ? <Check className="h-3.5 w-3.5 text-accent" /> : <CalendarPlus className="h-3.5 w-3.5" />}
              </button>
              <button
                data-testid={`publish-open-${p.id}-${clip.id}`}
                onClick={() => openPlatform(p)}
                className="flex items-center gap-1.5 border px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-white transition-colors hover:bg-secondary"
                style={{ borderColor: p.accent }}
              >
                <ExternalLink className="h-3.5 w-3.5" /> Post
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex gap-2">
        <button
          data-testid={`publish-download-${clip.id}`}
          onClick={downloadVideo}
          className="flex flex-1 items-center justify-center gap-1.5 border border-border py-2 text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-white"
        >
          <Download className="h-3.5 w-3.5" /> Download MP4
        </button>
      </div>

      {/* Manual date/time scheduler */}
      <div className="space-y-2 border border-border bg-background p-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent">Schedule at a custom time</p>
        <div className="flex flex-wrap gap-1.5">
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              data-testid={`publish-custom-plat-${p.id}-${clip.id}`}
              onClick={() => setCustomPlatform(p.id)}
              className={`px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-widest transition-colors ${
                customPlatform === p.id ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground hover:text-white"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            data-testid={`publish-custom-time-${clip.id}`}
            type="datetime-local"
            value={customTime}
            onChange={(e) => setCustomTime(e.target.value)}
            className="min-w-0 flex-1 border border-border bg-secondary px-2 py-2 font-mono text-xs text-white outline-none focus:border-accent"
          />
          <button
            data-testid={`publish-custom-add-${clip.id}`}
            onClick={scheduleCustom}
            className="flex items-center gap-1.5 bg-primary px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <CalendarPlus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
      </div>

      <p className="font-mono text-[10px] leading-relaxed text-muted-foreground/70">
        "Post" downloads the MP4, copies your caption, and opens the platform's upload page. The calendar icon uses the suggested best time; pick a custom date/time above to schedule exactly when you want (planning reminder — not auto-posting).
      </p>
    </div>
  );
};
